import { IJobContextIndexes } from "../contexts/JobContext"
import { IJob, IShiftStopIds, IStop } from "../entities/job/interface"

function getDestinationAndStopIndex(jobArray: IStop[][], currentJob: IJob, stage: string) {
    const currentJobId = currentJob._id

    const currentJobDestinationIndex = jobArray.findIndex((stopList: IStop[]) =>
        stopList.some((stop: IStop) => stop.type === stage && stop.jobId === currentJobId)
    )

    const currentJobStopIndex = jobArray[currentJobDestinationIndex]?.findIndex(
        (stop: IStop) => stop.type === stage && stop.jobId === currentJobId
    )

    return { currentJobDestinationIndex, currentJobStopIndex }
}

const isInvalidIndex = (index: number) => index === -1 || Number.isNaN(index)

export const getPreviousJobPart = (
    shiftStops: IShiftStopIds | null,
    setIndexes: (indexes: IJobContextIndexes) => void,
    currentJobDestinationIndex: number,
    currentJobStopIndex: number,
    currentJob: IJob,
    checkIfExist: boolean,
    isStopsNeed: boolean,
    isRoadMap: boolean
) => {
    const currentJobId = currentJob._id
    const key: string = currentJob.groupId ?? currentJobId

    if (!shiftStops || !shiftStops[key] || !shiftStops[key].destinationStops) {
        return null
    }

    const jobArray = shiftStops[key].destinationStops

    if (isInvalidIndex(currentJobDestinationIndex) || isInvalidIndex(currentJobStopIndex)) {
        return null
    }

    let previousDestinationIndex = currentJobDestinationIndex
    let previousStopIndex = currentJobStopIndex - 1

    if (previousStopIndex < 0) {
        // No previous stop for this destination, check for previous destination
        if (!previousDestinationIndex) {
            //No previous stop or destination
            return null
        } else {
            // There is a previous destination, get the last stop of the previous destination
            previousDestinationIndex--
            previousStopIndex = jobArray[previousDestinationIndex]?.length - 1
        }
    }

    if (isInvalidIndex(previousDestinationIndex) || isInvalidIndex(previousStopIndex)) {
        return null
    }

    if (checkIfExist) {
        return true
    }

    setIndexes({
        groupId: key,
        destinationIndex: isRoadMap ? currentJobDestinationIndex : previousDestinationIndex,
        stopIndex: previousStopIndex,
    })

    return isStopsNeed
        ? jobArray[previousDestinationIndex]
        : jobArray[previousDestinationIndex][previousStopIndex] || null
}

export function getNextJobPart(
    loadedJobs: IShiftStopIds | null,
    setIndexes: (indexes: IJobContextIndexes) => void,
    currentJob: IJob,
    stage: string
) {
    const currentJobId = currentJob._id
    const key: string = currentJob.groupId ?? currentJobId

    if (!loadedJobs || !loadedJobs[key] || !loadedJobs[key].destinationStops) {
        return null
    }

    const jobArray: IStop[][] = loadedJobs[key].destinationStops
    const { currentJobDestinationIndex, currentJobStopIndex } = getDestinationAndStopIndex(jobArray, currentJob, stage)

    if (currentJobDestinationIndex === -1 || currentJobStopIndex === -1) {
        return null
    }

    let nextDestinationIndex = currentJobDestinationIndex
    let nextStopIndex = currentJobStopIndex + 1

    if (currentJobStopIndex === jobArray[currentJobDestinationIndex].length - 1) {
        // No next stop for this destination, check for next destination
        if (nextDestinationIndex === jobArray.length - 1) {
            //No next stop or destination
            return null
        } else {
            // There is a previous destination, get the first stop of the previous destination
            nextDestinationIndex++
            nextStopIndex = 0
        }
    }

    setIndexes({
        groupId: key,
        destinationIndex: nextDestinationIndex,
        stopIndex: nextStopIndex,
    })
    return jobArray[nextDestinationIndex][nextStopIndex] || null
}

export function checkIfJobIsGrouped(loadedJobs: IShiftStopIds | null, currentJob: IJob) {
    const currentJobId = currentJob._id
    const key: string = currentJob.groupId ?? currentJobId

    if (!loadedJobs || !loadedJobs[key] || !loadedJobs[key].groupJob) {
        return false
    }
    return true
}

export const matchJobsWithStops = (jobs: IJob[], destinationStops: IStop[]) => {
    const jobIds = new Set(jobs.map((job) => job._id))

    const matchedStops: IStop[] = []
    const unmatchedStops: IStop[] = []

    destinationStops?.forEach((job) => {
        if (jobIds.has(job.jobId)) {
            matchedStops.push(job)
        } else {
            unmatchedStops.push(job)
        }
    })

    if (unmatchedStops.length === 0) {
        return [destinationStops]
    } else {
        return [matchedStops, unmatchedStops]
    }
}

export const updatedRoadMapStops = (
    job: IJob,
    shiftStops: IShiftStopIds | null,
    currentIndex: number,
    groupId: string | null
) => {
    if (!groupId) return null
    if (!shiftStops) return shiftStops

    const stops = shiftStops[groupId].destinationStops
    let foundStop = null
    let originalIndex = -1
    let stopPosition = -1

    // Find the stop and remove it
    for (let i = 0; i < stops.length; i++) {
        for (let j = 0; j < stops[i].length; j++) {
            if (stops[i][j].jobId === job._id && stops[i][j].type === job.type) {
                foundStop = stops[i][j]
                originalIndex = i
                stopPosition = j
                break
            }
        }
        if (foundStop) break
    }

    if (!foundStop) return shiftStops // If job stop not found, return original

    // Remove the stop from its original position
    stops[originalIndex].splice(stopPosition, 1)
    if (stops[originalIndex].length === 0) {
        stops.splice(originalIndex, 1) // Remove empty arrays
    }

    // Ensure currentIndex is within bounds
    while (stops.length <= currentIndex) {
        stops.push([]) // Fill empty arrays if needed
    }

    // Move existing stops at currentIndex to the next index
    stops.splice(currentIndex + 1, 0, stops[currentIndex])

    // Insert the found stop at the desired index
    stops[currentIndex] = [foundStop]

    return {
        ...shiftStops,
        [groupId]: {
            ...shiftStops[groupId],
            destinationStops: stops,
        },
    }
}

export const getNextStops = (
    stops: IShiftStopIds,
    groupId: string,
    currentDestinationIndex: number
): IStop[] | null => {
    // Check if the groupId exists in the stops object
    if (!stops[groupId]) {
        console.error(`Group ID ${groupId} not found in stops.`)
        return null
    }

    const destinationStops = stops[groupId].destinationStops

    // Calculate the index for the next destination
    const nextDestinationIndex = currentDestinationIndex + 1

    // Check if the next destination index is within bounds
    if (nextDestinationIndex >= destinationStops.length) {
        console.log("No more upcoming stops.")
        return null
    }

    // Return the next set of stops
    return destinationStops[nextDestinationIndex]
}

export const getPreviousStops = (
    stops: IShiftStopIds | null,
    groupId: string,
    currentDestinationIndex: number
): IStop[] | null => {
    // Check if the groupId exists in the stops object
    if (!stops || !stops[groupId]) {
        console.error(`Group ID ${groupId} not found in stops.`)
        return null
    }

    const destinationStops = stops[groupId].destinationStops

    // Calculate the index for the next destination
    const prevDestinationIndex = currentDestinationIndex - 1

    // Check if the next destination index is within bounds
    if (prevDestinationIndex < 0) {
        console.log("No more upcoming stops.")
        return null
    }

    // Return the next set of stops
    return destinationStops[prevDestinationIndex]
}

export const getCurrentJobPart = (
    shiftStops: IShiftStopIds | null,
    setIndexes: (indexes: IJobContextIndexes) => void,
    currentJobDestinationIndex: number,
    currentJobStopIndex: number,
    currentJob: IJob,
    checkIfExist: boolean
) => {
    const currentJobId = currentJob._id
    const key: string = currentJob.groupId ?? currentJobId

    if (!shiftStops || !shiftStops[key] || !shiftStops[key].destinationStops) {
        return null
    }

    const jobArray = shiftStops[key].destinationStops

    if (isInvalidIndex(currentJobDestinationIndex) || isInvalidIndex(currentJobStopIndex)) {
        return null
    }

    if (checkIfExist) {
        return true
    }

    setIndexes({
        groupId: key,
        destinationIndex: currentJobDestinationIndex,
        stopIndex: currentJobStopIndex,
    })

    return jobArray[currentJobDestinationIndex][currentJobStopIndex] || null
}
